import Button from 'Components/Base/Button'
import { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import useAppointments from 'hooks/useAppointments'
import useNewEnrollment from 'hooks/useNewEnrollment'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAccessToken } from 'reducers/authSlice'
import { loadProfile } from 'reducers/profileSlice'
import { setLoading } from 'reducers/tempSlice'
import styled, { useTheme } from 'styled-components'
import { AppointmentResponse } from 'types/Appointment'
import { formatTimeAndTimezone } from 'utilities/formatTimeAndTimezone'

interface props {
  ctl: boolean
  isInitial?: boolean
}

export default function EnrollmentConfirmation({ ctl, isInitial = true }: props) {
  const theme = useTheme()
  const [scheduledAppointment, setScheduledAppointment] = useState<string | undefined>()
  const [triaPerson, setTriaPerson] = useState<string>('Tria')

  const navigate = useNavigate()
  const token = useAppSelector(getAccessToken)
  const { getInitial } = useNewEnrollment({ token: token })
  const { getAppointments, getCTLAppointments } = useAppointments(token!)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setLoading(true))
    if (isInitial) {
      getInitial({ ctl: ctl, pa: !ctl })
        .then((res: AxiosResponse<{ scheduledAppointment: string; triaPerson: string }>) => {
          const { scheduledAppointment, triaPerson } = res.data
          triaPerson && setTriaPerson(triaPerson)
          scheduledAppointment && setScheduledAppointment(scheduledAppointment)
        })
        .then(() => dispatch(setLoading(false)))
        .catch(() => {
          dispatch(setLoading(false))
          navigate('/error')
        })
    } else {
      if (ctl) {
        getCTLAppointments()
          .then((res: AxiosResponse<AppointmentResponse>) => {
            setScheduledAppointment(transformDate(res.data.scheduledAppointment))
            setTriaPerson('your tria health coach')
          })
          .finally(() => dispatch(setLoading(false)))
      } else {
        getAppointments()
          .then((res: AxiosResponse<AppointmentResponse>) => {
            if (res.data.scheduledAppointment) {
              setScheduledAppointment(transformDate(res.data.scheduledAppointment))
              setTriaPerson('your tria health pharmacist')
              //setScheduledAppointment(res.data.scheduledAppointment)
            }
          })
          .finally(() => dispatch(setLoading(false)))
      }
    }
    if (token) {
      dispatch(loadProfile(token))
    }
  }, [])

  function transformDate(input) {
    // Example: "February 06, 2026 | 08:00 AM CST"
    const [datePart, timePart] = input.split(' | ')

    // Build a Date object
    const date = new Date(`${datePart} ${timePart}`)

    // Extract components
    const month = date.getMonth() + 1
    const day = date.getDate()
    const year = date.getFullYear()

    let hours = date.getHours()
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')

    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`
  }

  function formatDate(date) {
    return date.toISOString().replace(/-|:|\.\d+/g, '')
  }

  const downloadIcsFile = () => {
    const startDate = scheduledAppointment
    const endDate = dayjs(scheduledAppointment).add(1, 'hour').toString()
    //formatting must include no whitespace for ics file to download properly
    if (startDate && endDate && dayjs(startDate).format('MM/DD/YYYY') !== 'Invalid Date' && dayjs(endDate).format('MM/DD/YYYY') !== 'Invalid Date') {
      window.open(
        URL.createObjectURL(
          new Blob(
            [
              `
  BEGIN:VCALENDAR
  VERSION:2.0
  BEGIN:VEVENT
  DTSTAMP:${formatDate(new Date())}
  UID:${Math.random().toString(36).substring(2) + new Date().getTime().toString(36)}
  SUMMARY:Tria Health Appointment (phone call)
  DESCRIPTION:We will give you a call at the time and date listed. Please add our number to your contacts: 913-322-8456.
  DTSTART:${formatDate(new Date(startDate))}
  DTEND:${formatDate(new Date(endDate))}
  END:VEVENT
  END:VCALENDAR
  `,
            ],
            { type: 'text/calendar' },
          ),
        ),
      )
    }
  }

  return (
    <Container>
      <Card>
        <Header>You’re All Set!</Header>
        <Sub>Thanks for the info! It helps us make sure your appointment is as smooth and helpful as possible.</Sub>
        <Para>
          <b>Here’s what’s next: </b>
        </Para>
        <Para>
          You’re scheduled to meet with <b>{triaPerson}</b> on <b>{dayjs(scheduledAppointment).format('MMM DD, YYYY')}</b> at{' '}
          <b>{formatTimeAndTimezone(scheduledAppointment ?? '')}</b>.
        </Para>
        <Para>We’ll give you a call at that time from a 913 area code.</Para>
        <Para>We’re excited to chat with you. Talk soon!</Para>
        <Button text="Add to My Calendar" onClick={() => downloadIcsFile()} backgroundColor={theme.colors.status.good} styles={{ marginTop: 30 }} />
      </Card>
      <Image src="https://triacentral.blob.core.windows.net/dev-storage/portal/enrollment/Intake%20Form%20Images%201.png" />
    </Container>
  )
}

const Container = styled.div`
  font-family: Raleway;
  display: flex;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  height: fit-content;
  justify-content: space-between;
  margin-top: 60px;
  @media only screen and (max-width: 600px) {
    margin-top: 20px;
  }
`

const Card = styled.div`
  font-family: Raleway;
  max-width: 600px;
  min-width: 600px;
  height: fit-content;
  min-height: 640px;
  padding: 40px;
  text-align: left;
  @media only screen and (max-width: 600px) {
    padding: 0px 20px 40px;
    margin-top: 0px;
    min-height: fit-content;
    max-width: 100%;
    min-width: 100%;
  }
`

const Header = styled.h1`
  font-weight: 700;
  font-size: 40px;
  margin-bottom: 20px;
`
const Sub = styled.p`
  font-size: 18px;
  margin-bottom: 40px;
`

const Para = styled.p`
  font-size: 18px;
  margin-bottom: 20px;
`

const Image = styled.img`
  max-height: 530px;
  padding: 40px;
  @media only screen and (max-width: 1200px) {
    display: none;
  }
`
